import _ from 'lodash'
import * as THREE from 'three'

import Config from '@glass-designer/config'
import { EditorUserError } from './../utils/editorErrors'
import MathsUtils from '@/utils/mathsUtils'
import EditorDatumUtils from './../utils/editorDatumUtils'
import DatumLink from './datumLink'
import EditorUtils from './../utils/editorUtils'
import BaseObject from '../objects/baseObject'
import LineObject from '../library/three/LineObject'

import GlassDesignerTypes from '../types'


/*
 * GlassPanelItem
 * A miscellaneous panel item on the glass dragged from the library
 * Can be anything, including toughening stamp, safety markers, finger grips, etc etc
 */
export default class GlassPanelItem extends BaseObject {
  constructor(editor, itemData = {}) {
    super(editor)
    this.objectClassName = 'GlassPanelItem'

    this.worldPoint = new THREE.Vector2(0, 0)
    this.anchorPoint = new THREE.Vector2(0, 0)
    this.datumLinks = []

    // Anchor measure to types. Fixed to center
    // Not editable currently, but here for future use
    this.measureToHorizontal = GlassDesignerTypes.HoleMeasurePointTypes.center
    this.measureToVertical = GlassDesignerTypes.HoleMeasurePointTypes.middle

    // Rendering details
    this.sceneObject = new THREE.Object3D()
    this.editor.scene.add(this.sceneObject)

    this.itemObj = null
    this.objectGeometry = null
    this.selectedLineObj = null

    this.width = parseFloat(itemData.width || 100)
    this.height = parseFloat(itemData.height || 100)
    this.angle = parseFloat(itemData.angle || 0)
    this.stockId = parseInt(itemData.stockId || 0)
    this.orderEditorLibraryItemId = parseInt(itemData.orderEditorLibraryItemId || 0)

    // Special flags - Order Stamp Item
    // Created automatically based on the order stamp rules
    this.isOrderStampItem = !!itemData.isOrderStampItem

    this.listenMouseEvents = true

    this.updateSceneObject()
  }


  /**
   * Dispose the object
   */
  dispose() {
    super.dispose()

    // Remove stuff
    if(this.objectGeometry) {
      this.objectGeometry.dispose()
      this.objectGeometry = null
    }
    this.itemObj = null

    if(this.selectedLineObj) {
      this.selectedLineObj.dispose()
      this.selectedLineObj = null
    }
  }


  /**
   * onBeforeRender
   * Called prior to rendering of the object
   */
  onBeforeRender() {
    super.onBeforeRender()

    // Selected support
    if(this.selectedLineObj) {
      this.selectedLineObj.updateResolution(this.editor.settings.viewportWidth, this.editor.settings.viewportHeight)
      this.selectedLineObj.setVisible(this.selected)
    }
  }


  /**
   * Mouse Move Event
   */
  onMouseMoveEvent(ev, mouseData, intersectionData = {}) {
    if(this.editor.isSelectEnabled())
      this.editor.setMouseCursorHand()
  }


  /**
   * Update the view level on the object
   * Adjusts the scene object matrix & visibility for editing / viewing modes
   */
  viewLevelUpdated(viewLevel) {
    // Hide the panel items for certain view modes
    let visible = (viewLevel == GlassDesignerTypes.EditorViewLevelTypes.normal || viewLevel == GlassDesignerTypes.EditorViewLevelTypes.technical)

    if(this.itemObj)
      this.itemObj.visible = visible
  }


  /**
   * Calculate the bounds of the panel item
   */
  calculateBounds() {
    return new THREE.Box2(
      new THREE.Vector2(this.worldPoint.x - this.width / 2, this.worldPoint.y - this.height / 2),
      new THREE.Vector2(this.worldPoint.x + this.width / 2, this.worldPoint.y + this.height / 2)
    )
  }


  /**
   * Set the list of datum links
   * Typically links to 2 other objects (segments in the shape and/or holes)
   */
  setDatumLinks(datumLinks) {
    this.datumLinks = datumLinks.map(datum => datum.clone())
    this.updateSceneObject()
  }


  /**
   * Check if any of the datum links are flagged as invalid
   */
  hasInvalidDatumLink() {
    return this.datumLinks.filter(datum => !datum.isValidDatumLink).length > 0 || this.datumLinks.length == 0
  }


  /**
   * Get a datum link for the panel item given an ID
   * Returns null if no datum link found
   */
  getDatumLinkById(id) {
    return this.datumLinks.find(link => link.id == id)
  }


  /**
   * Calculate the world position the datum links resolve to
   * Requires 2 datum points set
   */
  updateDatumLinkWorldPoint(datumOptions = {}) {
    let point = EditorDatumUtils.calculateDatumLinksWorldPoint(this.editor, this.datumLinks, datumOptions)

    // Don't update if invalid. Datum links will be flagged invalid internally
    // Other functions will handle resetting the datum links if required
    if(point)
      this.worldPoint.copy(point)
  }


  /**
   * Update the anchor point based on the measure to types
   */
  setAnchorInfo(offsetHorizontalType, offsetVerticalType) {
    this.measureToHorizontal = offsetHorizontalType
    this.measureToVertical = offsetVerticalType

    // Calculate the point based on bounds
    // Note: Anchor points are relative to the panel item
    let bounds = this.calculateBounds()
    let offsetPoint = MathsUtils.calculateHoleOffsetPoint(bounds, this.measureToHorizontal, this.measureToVertical)

    this.anchorPoint.x = offsetPoint.x
    this.anchorPoint.y = offsetPoint.y

    this.updateSceneObject()
  }


  /**
   * Calculate the anchor point in world coordinates
   */
  calculateAnchorWorldPoint() {
    let bounds = this.calculateBounds()

    return new THREE.Vector2(bounds.min.x + this.anchorPoint.x, bounds.min.y + this.anchorPoint.y)
  }


  /**
   * Update the scene object
   */
  updateSceneObject() {
    if(this.selectedLineObj) {
      this.selectedLineObj.dispose()
      this.selectedLineObj = null
    }
    if(this.objectGeometry) {
      this.objectGeometry.dispose()
      this.objectGeometry = null
    }

    this.clearSceneObject()
    this.updateDatumLinkWorldPoint()

    var texture = null
    if(this.orderEditorLibraryItemId) {
      texture = this.editor.loadEditorLibraryItemTextureSync(this.orderEditorLibraryItemId)
      if(!texture)
        return
    }

    // If a stamp, load the image from the order stamp config
    if(this.isOrderStampItem) {
      texture = this.editor.loadOrderStampTextureSync()
      if(!texture)
        return
    }


    this.objectGeometry = new THREE.BoxGeometry(this.width, this.height, 5)
    let objectMaterial = new THREE.MeshBasicMaterial({
      map: texture,
      color: '#FFFFFF',
      transparent: true,
      depthWrite: false,
      depthTest: false
    })

    this.itemObj = new THREE.Mesh(this.objectGeometry, objectMaterial)

    // Setup pos
    this.itemObj.position.set(this.worldPoint.x, this.worldPoint.y, 200)
    this.itemObj.scale.x = -1    // Flip around for the texture adjustment
    this.itemObj.scale.y = -1
    this.sceneObject.add(this.itemObj)

    // Selection object
    let itemPoints = MathsUtils.generateRotatedRectanglePolygon(this.angle, this.worldPoint, this.width, this.height)
    itemPoints.push(itemPoints[0].clone())

    this.selectedLineObj = new LineObject({
      colour: this.editor.getTheme().selectionColour,
      width: this.editor.getTheme().selectionWidth,
    })
    this.selectedLineObj.updateLinePath(itemPoints)
    this.selectedLineObj.setVisible(this.selected)
    this.sceneObject.add(this.selectedLineObj.getThreeObject())
  }


  /**
   * Check for intersections between the object and a ray
   * Returns an object containing intersection point & object position
   * Returns null if no intersection occurred
   */
  checkRayIntersection(ray, worldRadius) {
    // Ignore if not visible
    if(!this.isVisible())
      return null

    var closestIntersection = null
    let radiusTest = Math.max(worldRadius, 0.1)
    
    // Create a plane from the item
    // Assumes the 2D shape is sitting on the plane at origin (Z = 0)
    var shapePlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
    var rayInt = new THREE.Vector3()
    if(!ray.intersectPlane(shapePlane, rayInt))
      return null

    var ray2DPoint = new THREE.Vector2(rayInt.x, rayInt.y)

    let rads = MathsUtils.degreesToRadians(this.angle)
    let fwdDir = MathsUtils.generate2DDirectionVector(rads - Math.PI / 2)
    let rightDir = MathsUtils.generate2DDirectionVector(rads)

    let fwdDist = MathsUtils.calculateDistanceToPlane2D(fwdDir, this.worldPoint, ray2DPoint)
    let rightDist = MathsUtils.calculateDistanceToPlane2D(rightDir, this.worldPoint, ray2DPoint)

    // Inside object?
    if(Math.abs(fwdDist) < this.height/2 && Math.abs(rightDist) < this.width/2) {
      closestIntersection = {
        intersection: ray2DPoint,
        position: ray2DPoint,
        objectId: this.id,
        distance: 0
      } 
    // Within radius?
    } else if(Math.abs(fwdDist) < this.height/2 + radiusTest && Math.abs(rightDist) < this.width/2 + radiusTest) {
      let dist = Math.min(Math.abs(fwdDist) - this.height/2, Math.abs(rightDist) - this.width/2)

      closestIntersection = {
        intersection: ray2DPoint,
        position: ray2DPoint,
        objectId: this.id,
        distance: dist
      }
    }

    return closestIntersection
  }

  /**
   * Clone the GlassDatumLine
   */
  clone() {
    var newPanelItem = new GlassPanelItem(this.editor, {
      width: this.width,
      height: this.height,
      angle: this.angle,
      stockId: this.stockId,
      orderEditorLibraryItemId: this.orderEditorLibraryItemId,
      isOrderStampItem: this.isOrderStampItem
    })

    // Note: Datum links will clone the IDs & may require adjusting by code using the clone
    newPanelItem.anchorPoint = this.anchorPoint.clone()
    newPanelItem.datumLinks = this.datumLinks.map(d => d.clone())

    return newPanelItem
  }


  // Serialize/Deserialize to/from a JSON object
  serialize() {    
    var outputData = super.serialize()

    outputData.anchorPoint = MathsUtils.serializeVector2(this.anchorPoint)
    outputData.worldPoint = MathsUtils.serializeVector2(this.worldPoint)
    outputData.datumLinks = this.datumLinks.map(datum => datum.serialize())
    outputData.measureToHorizontal = this.measureToHorizontal
    outputData.measureToVertical = this.measureToVertical

    outputData.width = this.width
    outputData.height = this.height
    outputData.angle = this.angle
    outputData.stockId = this.stockId
    outputData.orderEditorLibraryItemId = this.orderEditorLibraryItemId
    outputData.isOrderStampItem = this.isOrderStampItem

    return outputData
  }
  deserialize(object) {
    super.deserialize(object)

    this.anchorPoint = MathsUtils.deserializeVector2(object.anchorPoint)
    this.worldPoint = MathsUtils.deserializeVector2(object.worldPoint)
    
    this.datumLinks = (object.datumLinks || []).map(datumData => {
      var datum = new DatumLink()
      datum.deserialize(datumData)
      return datum
    })

    this.width = parseFloat(object.width || 100)
    this.height = parseFloat(object.height || 100)
    this.angle = parseFloat(object.angle || 0)
    this.stockId = parseInt(object.stockId || 0)
    this.orderEditorLibraryItemId = parseInt(object.orderEditorLibraryItemId || 0)
    this.measureToHorizontal = object.measureToHorizontal || GlassDesignerTypes.HoleMeasurePointTypes.center
    this.measureToVertical = object.measureToVertical || GlassDesignerTypes.HoleMeasurePointTypes.middle

    this.isOrderStampItem = !!object.isOrderStampItem

    this.updateSceneObject()
  }

}